import { DocumentNode } from "@apollo/client";
import {
  GET_LIST_TICKETS,
  GET_MANAGE_TICKETS,
  GET_SOLD_TICKETS,
  GET_DELIST_AND_RETURN_TICKETS,
  GET_DELIST_AND_UNSOLD_TICKETS,
  GET_LISTING_REQUEST_CANCELLED_TICKETS,
} from "../ManageTickets/api/TicketsQuery";
import { MANAGE_TICKETS_ORDER_BY } from "../ManageTickets/manageTicketSort";

export type CampaignRecipientMode = "all" | "select" | "filter";

export type CampaignTicketTab =
  | ""
  | "Verification"
  | "Listed"
  | "Sold"
  | "Delisted"
  | "Unsold"
  | "Listing Request Cancelled";

export type CampaignDatePreset = "today" | "tomorrow" | "next7days" | "custom" | null;

export interface CampaignTeamOption {
  value: string;
  label: string;
}

export interface CampaignRecipientFilterState {
  selectedTeam: string;
  ticketTab: CampaignTicketTab;
  datePreset: CampaignDatePreset;
  customStartDate: string;
  customEndDate: string;
}

export interface CampaignFilterUser {
  u_id: string;
  u_email_id: string;
  u_full_name?: string | null;
  u_first_name?: string | null;
  u_last_name?: string | null;
  u_email_enabled?: boolean | null;
  u_notification_enabled?: boolean | null;
  u_device_tokens?: string[] | null;
  u_affiliate_id?: string | null;
  u_sales_reps_id?: string | null;
  u_teams?: string[] | null;
  u_is_active?: boolean | null;
  affiliate?: {
    a_id?: string | null;
    a_email_id?: string | null;
  } | null;
  user_preference?: Array<{
    lc_id?: string | null;
    league_category?: {
      lc_id?: string | null;
      lc_name?: string | null;
      lc_abbrev?: string | null;
    } | null;
  }> | null;
}

export const DEFAULT_CAMPAIGN_RECIPIENT_FILTERS: CampaignRecipientFilterState = {
  selectedTeam: "",
  ticketTab: "",
  datePreset: null,
  customStartDate: "",
  customEndDate: "",
};

export const CAMPAIGN_TICKET_TAB_OPTIONS: Array<{ value: CampaignTicketTab; label: string }> = [
  { value: "", label: "Any ticket status" },
  { value: "Verification", label: "Verification" },
  { value: "Listed", label: "Listed" },
  { value: "Sold", label: "Sold" },
  { value: "Delisted", label: "Delisted" },
  { value: "Unsold", label: "Unsold" },
  { value: "Listing Request Cancelled", label: "Listing Request Cancelled" },
];

const TICKET_TAB_QUERY_CONFIG: Record<
  Exclude<CampaignTicketTab, "">,
  { query: DocumentNode; resultKey: string }
> = {
  Verification: { query: GET_MANAGE_TICKETS, resultKey: "filtermanagetickets" },
  Listed: { query: GET_LIST_TICKETS, resultKey: "filterlisttickets" },
  Sold: { query: GET_SOLD_TICKETS, resultKey: "FilterSoldTickets" },
  Delisted: { query: GET_DELIST_AND_RETURN_TICKETS, resultKey: "filterreturntickets" },
  Unsold: { query: GET_DELIST_AND_UNSOLD_TICKETS, resultKey: "filterUnsoldTickets" },
  "Listing Request Cancelled": {
    query: GET_LISTING_REQUEST_CANCELLED_TICKETS,
    resultKey: "filterListingRequestCancelledTickets",
  },
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getTodayStr = (): string => formatLocalDate(new Date());

export const getTomorrowStr = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return formatLocalDate(tomorrow);
};

export const getNext7DaysEndStr = (): string => {
  const end = new Date();
  end.setDate(end.getDate() + 6);
  return formatLocalDate(end);
};

export const resolveCampaignDateRange = (
  filters: CampaignRecipientFilterState,
): { startdate: string | null; enddate: string | null } => {
  if (!filters.ticketTab) {
    return { startdate: null, enddate: null };
  }

  if (filters.datePreset === "today") {
    const today = getTodayStr();
    return { startdate: today, enddate: today };
  }
  if (filters.datePreset === "tomorrow") {
    const tomorrow = getTomorrowStr();
    return { startdate: tomorrow, enddate: tomorrow };
  }
  if (filters.datePreset === "next7days") {
    return { startdate: getTodayStr(), enddate: getNext7DaysEndStr() };
  }
  if (filters.datePreset === "custom" && filters.customStartDate && filters.customEndDate) {
    return { startdate: filters.customStartDate, enddate: filters.customEndDate };
  }

  return { startdate: null, enddate: null };
};

export const hasActiveCampaignRecipientFilters = (filters: CampaignRecipientFilterState): boolean =>
  Boolean(filters.selectedTeam || filters.ticketTab);

export const userMatchesTeamFilter = (
  user: CampaignFilterUser,
  selectedTeam: string,
  teamsList: CampaignTeamOption[],
): boolean => {
  if (!selectedTeam) return true;

  const userTeams: string[] = Array.isArray(user.u_teams) ? user.u_teams : [];
  const prefObjArr = Array.isArray(user.user_preference) ? user.user_preference : [];

  const selectedTeamLower = String(selectedTeam).toLowerCase();
  const selTeamObj = teamsList.find((team) => team.value === selectedTeam);
  const selLabel = selTeamObj?.label || "";
  const selName = selLabel.replace(/\s*\(.+\)$/, "").trim();
  const selAbbrevMatch = selLabel.match(/\(([^)]+)\)$/);
  const selAbbrev = selAbbrevMatch ? selAbbrevMatch[1] : "";
  const selNameLower = selName.toLowerCase();
  const selAbbrevLower = selAbbrev.toLowerCase();

  const matchesPrefById = prefObjArr.some(
    (pref) => pref?.lc_id === selectedTeam || pref?.league_category?.lc_id === selectedTeam,
  );

  const matchesPrefByName = prefObjArr.some((pref) => {
    const name = (pref?.league_category?.lc_name || "").toLowerCase();
    const abbrev = (pref?.league_category?.lc_abbrev || "").toLowerCase();
    return (
      selectedTeamLower === name ||
      selectedTeamLower === abbrev ||
      selNameLower === name ||
      selNameLower === abbrev ||
      selAbbrevLower === name ||
      selAbbrevLower === abbrev
    );
  });

  const matchesUserTeams = userTeams.some((teamName) => {
    const lowerTeamName = String(teamName).toLowerCase();
    return (
      lowerTeamName === selectedTeamLower ||
      lowerTeamName === selNameLower ||
      lowerTeamName === selAbbrevLower
    );
  });

  return matchesPrefById || matchesPrefByName || matchesUserTeams;
};

export const applyCampaignUserScope = (
  users: CampaignFilterUser[],
  options: {
    isAffiliateAdmin: boolean;
    isMarketingUser: boolean;
    scopedAffiliateId: string | null;
    loggedInEmail: string;
    scopedSalesRepId: string | null;
  },
): CampaignFilterUser[] => {
  return users.filter((user) => {
    if (user.u_is_active === false) return false;

    if (options.isMarketingUser) {
      if (!options.scopedSalesRepId) return false;
      if (user.u_sales_reps_id !== options.scopedSalesRepId) return false;
    }

    if (options.isAffiliateAdmin) {
      const userAffiliateId = user.u_affiliate_id || user.affiliate?.a_id || null;
      const userAffiliateEmail = (user.affiliate?.a_email_id || "").toLowerCase().trim();
      if (options.scopedAffiliateId && userAffiliateId === options.scopedAffiliateId) {
        return true;
      }
      if (options.loggedInEmail && userAffiliateEmail === options.loggedInEmail) {
        return true;
      }
      return false;
    }

    return true;
  });
};

export const buildTicketFilterQueryVariables = (filters: CampaignRecipientFilterState) => {
  const { startdate, enddate } = resolveCampaignDateRange(filters);
  const baseVars: Record<string, unknown> = {
    pageSize: 5000,
    pageOffset: 0,
    search_event: "%",
    search_client: "%",
    pricingMode: "%",
    order_by: MANAGE_TICKETS_ORDER_BY,
    ticketStatus: null,
    leagueId: null,
  };

  if (startdate) baseVars.startdate = startdate;
  if (enddate) baseVars.enddate = enddate;

  if (filters.ticketTab === "Sold") {
    baseVars.paymentStatus = null;
  }

  return baseVars;
};

export const getTicketRowsFromQueryResult = (
  ticketTab: CampaignTicketTab,
  data: Record<string, unknown> | null | undefined,
): Array<Record<string, unknown>> => {
  if (!ticketTab || !data) return [];
  const config = TICKET_TAB_QUERY_CONFIG[ticketTab];
  if (!config) return [];
  const rows = data[config.resultKey];
  return Array.isArray(rows) ? rows : [];
};

export const getTicketFilterQuery = (ticketTab: CampaignTicketTab): DocumentNode | null => {
  if (!ticketTab) return null;
  return TICKET_TAB_QUERY_CONFIG[ticketTab]?.query || null;
};

export const ticketRowsToUserIds = (rows: Array<Record<string, unknown>>): string[] => {
  const ids = new Set<string>();
  rows.forEach((row) => {
    const userId = String(row.u_id || "").trim();
    if (userId) ids.add(userId);
  });
  return Array.from(ids);
};
